import { useCallback, useState } from 'react';
import { ApiResponseError } from '@/shared/types/api';
import { postMetricEvent } from '@/shared/services/event-metrics';
import { EVENT_LABELS } from '@/shared/types/event-metrics';
import useStorage from '@/shared/hooks/use-storage';
import {
  DATASET_QA_TICKET_VALIDATION_STORAGE_KEY,
  DATASET_QA_WORK_SESSION_TTL_SECONDS,
} from '@/features/datasets/constants';
import { createMetricEventPayload } from '@/shared/utils/metrics-helper';
import { reportError, reportWarning } from '@/shared/services/sentry';

interface DatasetTicketValidationState {
  startedAt: number;
}

const keyForId = (taskId: number) => `${DATASET_QA_TICKET_VALIDATION_STORAGE_KEY}-${taskId}`;

const readState = (taskId: number): DatasetTicketValidationState | null => {
  const raw = sessionStorage.getItem(keyForId(taskId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DatasetTicketValidationState;
  } catch {
    return null;
  }
};

/** Drops the starts left by a session that ended before its tickets were resolved. */
export const clearAllDatasetTicketValidationState = () => {
  try {
    const prefix = `${DATASET_QA_TICKET_VALIDATION_STORAGE_KEY}-`;
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  } catch (err: unknown) {
    reportError(err, { feature: 'datasets', action: 'validation_start_clear_error' });
  }
};

const usePublishQADatasetValidationEvent = () => {
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [error, setError] = useState<ApiResponseError | null>(null);

  const { store } = useStorage();

  /** Keeps the start of a ticket already in progress. */
  const registerValidationStart = useCallback((taskId: number) => {
    try {
      if (readState(taskId) === null) {
        sessionStorage.setItem(keyForId(taskId), JSON.stringify({ startedAt: Date.now() }));
      }
    } catch (err: unknown) {
      reportError(err, { feature: 'datasets', action: 'validation_start_register_error' });
    }
  }, []);

  /**
   * Calculates the duration in seconds between start time and the end time, rounded to 2 decimal places
   */
  const calculateDuration = (startTime: number, endTime: number): number => {
    const durationInSeconds = (endTime - startTime) / 1000;
    return Math.round(durationInSeconds * 100) / 100;
  };

  /**
   * Creates common tags for metric events
   * @param userEmail - The email of the user
   * @returns A tags object with operator
   */
  const createMetricTags = (userEmail: string) => ({
    operator: userEmail,
  });

  /**
   * Publishes the Validation Events
   * @param taskId - The ID of the dataset task
   * @param numberOfImagesValidated - The number of images validated
   */
  const publishValidationEvent = async (taskId: number, numberOfImagesValidated: number, finishedAt = Date.now()) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const state = readState(taskId);
      sessionStorage.removeItem(keyForId(taskId));

      if (state === null) {
        reportWarning(`No start time found for dataset task ${taskId}`, {
          feature: 'datasets',
          action: 'validation_start_time_missing',
        });
        return;
      }

      const duration = calculateDuration(state.startedAt, finishedAt);
      if (duration > DATASET_QA_WORK_SESSION_TTL_SECONDS) {
        reportWarning(
          `Dataset ticket ${taskId} validation duration (${duration}s) exceeds the work session TTL (${DATASET_QA_WORK_SESSION_TTL_SECONDS}s); publishing capped at the TTL`,
          { feature: 'datasets', action: 'validation_duration_exceeds_session_ttl' }
        );
      }
      const authData = await store.getAuth();
      const userEmail = authData?.user.email || 'unknown_user';
      const tags = createMetricTags(userEmail);

      const metricEvents = [
        createMetricEventPayload(
          EVENT_LABELS.DATASET_QA_VALIDATION_DURATION_SECONDS,
          Math.min(duration, DATASET_QA_WORK_SESSION_TTL_SECONDS),
          tags
        ),
        createMetricEventPayload(EVENT_LABELS.DATASET_QA_VALIDATION_NUMBER_OF_IMAGES, numberOfImagesValidated, tags),
      ];

      metricEvents.forEach((event) => {
        void postMetricEvent(event);
      });
      setSuccess(true);
    } catch (err: unknown) {
      reportError(err, { feature: 'datasets', action: 'validation_event_unknown_error' });
      if (err && typeof err === 'object' && 'message' in err) {
        setError(err as ApiResponseError);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Publishes a delete images event
   * @param count - The number of images deleted
   */
  const publishImageDeletedEvent = async (count: number) => {
    if (count <= 0) return;
    try {
      const authData = await store.getAuth();
      const userEmail = authData?.user.email || 'unknown_user';
      const tags = createMetricTags(userEmail);

      const metricEvent = createMetricEventPayload(EVENT_LABELS.DATASET_QA_IMAGE_DELETED, count, tags);

      void postMetricEvent(metricEvent);
    } catch (err: unknown) {
      reportError(err, { feature: 'datasets', action: 'image_deleted_event' });
    }
  };

  /**
   * Publishes an undelete images event
   * @param count - The number of images undeleted
   */
  const publishImageUndeletedEvent = async (count: number) => {
    if (count <= 0) return;
    try {
      const authData = await store.getAuth();
      const userEmail = authData?.user.email || 'unknown_user';
      const tags = createMetricTags(userEmail);
      const metricEvent = createMetricEventPayload(EVENT_LABELS.DATASET_QA_IMAGE_UNDELETED, count, tags);
      void postMetricEvent(metricEvent);
    } catch (err: unknown) {
      reportError(err, { feature: 'datasets', action: 'image_undeleted_event' });
    }
  };

  return {
    registerValidationStart,
    publishValidationEvent,
    publishImageDeletedEvent,
    publishImageUndeletedEvent,
    isLoading,
    isSuccess,
    error,
    hasError: !!error,
  };
};

export default usePublishQADatasetValidationEvent;
